import { Link } from 'react-router';
import { formatCurrency, formatOrderDisplayId } from '~/utils';
import type { Money, UnitCode } from '~/graphql/graphql';
import {
  DashboardColumn,
  DashboardColumnSpinner,
} from '~/pages/dashboard/sections/DashboardColumn';
// Minimal structural type satisfied by both getOrders and shippingSoon results.
export type ShipmentOrder = {
  id: string;
  purchaseOrderNumber?: string | null;
  status: string;
  createdAt?: string | null;
  deliveryStartDate?: string | null;
  deliveryEndDate?: string | null;
  shippedDate?: string | null;
  sellingOrganization: {
    name: string;
    sellingOrganizationCode?: string | null;
  };
  buyingOrganization?: { name: string } | null;
  currency?: string | null;
  items: Array<{
    quantity: { value: number; unit: UnitCode };
    price?: Money | null;
    suggestedPrice?: Money | null;
    effectivePrice?: Money | null;
    quantityStored?: { value: number } | null;
    pricePerPiece?: Pick<Money, 'amount'> | null;
    platformProductsLengths?: {
      isNominal?: boolean | null;
      platformProduct?: {
        name?: string;
        piecesPerBundle?: { value: number } | null;
        actualWidthInches?: number | null;
        actualThicknessInches?: number | null;
        nominalWidthInches?: number | null;
        nominalThicknessInches?: number | null;
        actualLengthInches?: number | null;
      } | null;
      length?: {
        actualLengthInches?: number | null;
        nominalLengthInches?: number | null;
      } | null;
    } | null;
  }>;
};

interface ShipmentOrdersColumnProps {
  title: string;
  subtitle: string;
  badge?: number;
  footerLink: string;
  footerLabel: string;
  orders: ShipmentOrder[];
  isLoading: boolean;
  isError: boolean;
  emptyText: string;
  errorText: string;
  getOrgLabel: (order: ShipmentOrder) => string;
  renderRowTopRight: (order: ShipmentOrder) => React.ReactNode;
  renderDateMeta: (order: ShipmentOrder) => {
    row3Right: React.ReactNode;
    row4Right: React.ReactNode;
  };
  renderAction?: (order: ShipmentOrder) => React.ReactNode;
  showPrice?: boolean;
}

export function ShipmentOrdersColumn({
  title,
  subtitle,
  badge,
  footerLink,
  footerLabel,
  orders,
  isLoading,
  isError,
  emptyText,
  errorText,
  getOrgLabel,
  renderRowTopRight,
  renderDateMeta,
  renderAction,
  showPrice = true,
}: ShipmentOrdersColumnProps) {
  let body: React.ReactNode;

  if (isLoading) {
    body = <DashboardColumnSpinner />;
  } else if (isError) {
    body = (
      <p className="text-sm font-medium text-neut-sec text-center px-6 py-8">
        {errorText}
      </p>
    );
  } else if (orders.length === 0) {
    body = (
      <p className="text-sm font-medium text-neut-sec px-6 py-8">{emptyText}</p>
    );
  } else {
    body = orders.map((order) => {
      const primaryItem = order.items[0];
      const primaryProductName =
        primaryItem?.platformProductsLengths?.platformProduct?.name;
      const { row3Right, row4Right } = renderDateMeta(order);

      return (
        <div
          key={order.id}
          className="flex flex-col gap-1 px-6 py-4 border-b border-neut-border last:border-b-0"
        >
          <div className="flex items-center justify-between gap-2">
            <Link
              to={`/orders/view/${order.id}?returnPath=/dashboard`}
              className="text-sm font-medium text-prim hover:text-prim-hover"
            >
              #{formatOrderDisplayId(order)}
            </Link>
            {renderRowTopRight(order)}
          </div>
          <p className="text-xs font-medium text-neut-sec">
            {getOrgLabel(order)}
          </p>
          {primaryItem && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-neut-prim">
                  {primaryProductName ?? '—'}
                </p>
                {row3Right}
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-neut-sec">
                  {primaryItem.quantity.value} {primaryItem.quantity.unit}
                  {showPrice && primaryItem.effectivePrice != null && (
                    <>
                      {' · '}
                      {formatCurrency(
                        primaryItem.effectivePrice.amount,
                        order.currency ?? primaryItem.effectivePrice.currency
                      )}
                    </>
                  )}
                </p>
                {row4Right}
              </div>
            </>
          )}
          {renderAction?.(order)}
        </div>
      );
    });
  }

  return (
    <DashboardColumn
      title={title}
      subtitle={subtitle}
      badge={badge}
      footerLink={footerLink}
      footerLabel={footerLabel}
    >
      {body}
    </DashboardColumn>
  );
}
